import { Pipe, PipeTransform } from '@angular/core';
import { IProductos } from '../../../private/shared/models/productos';
import { OperationsService } from '../../services/operations/operations.service';

/**
 * Interfaz que representa el resumen de precios de productos
 * total_precio_descuento: Precio final con descuento aplicado
 * total_precio: Precio original sin descuento
 * total_descuento: Monto del descuento (valor negativo)
 */
export interface ISummaryPriceResult {
  total_precio_descuento: number;
  total_precio: number;
  total_descuento: number;
  total_productos: number;
  puntos_recompepsi: number;
}

/**
 * Pipe para calcular el resumen de precios de un array de productos
 * Aplica tipo de cambio, redondeo a 2 decimales y calcula descuentos
 * 
 * @usageNotes
 * ```html
 * <!-- Uso básico -->
 * {{ productos | summaryPrice | json }}
 * 
 * <!-- Con tipo de cambio -->
 * {{ productos | summaryPrice : 1.5 | json }}
 * 
 * <!-- Accediendo a valores específicos -->
 * {{ (productos | summaryPrice).total_precio_descuento | currency }}
 * ```
 */
@Pipe({
  name: 'summaryPrice'
})
export class SummaryPricePipe implements PipeTransform {

  constructor(private operationsS: OperationsService) { }

  /**
   * Calcula el resumen de precios de un array de productos
   * @param products Array de productos a procesar
   * @param exchangeRate Tipo de cambio (por defecto 1)
   * @param forPrice_info Si es true, retorna el precio con descuento, si es false, retorna el precio original
   * @returns Objeto con los precios calculados y redondeados
   */
  transform(products: IProductos[], exchangeRate: number = 1, forPriceInfo: boolean = false): ISummaryPriceResult {
    if (!products || products.length < 1) {
      return {
        total_precio_descuento: 0,
        total_precio: 0,
        total_descuento: 0,
        total_productos: 0,
        puntos_recompepsi: 0
      };
    }

    if (forPriceInfo) {
      return products.map((product: IProductos) => ({
        total_precio: (product.precio_original || 0) * (product.productos || 0),
        total_descuento: (product.precio_original || 0) * (product.productos || 0) - (product.price_info || (product.precio || 0) * (product.productos || 0)),
        total_precio_descuento: product.price_info || (product.precio || 0) * (product.productos || 0),
        total_productos: product.productos || 0,
        puntos_recompepsi: (product.puntos_recompepsi || 0) * (product.productos || 0)
      }))
        .reduce((a: ISummaryPriceResult, b: ISummaryPriceResult) => ({
          total_precio: a.total_precio + b.total_precio,
          total_descuento: a.total_descuento + b.total_descuento,
          total_precio_descuento: a.total_precio_descuento + b.total_precio_descuento,
          total_productos: a.total_productos + b.total_productos,
          puntos_recompepsi: a.puntos_recompepsi + b.puntos_recompepsi
        }));
    }

    return products.map((product: IProductos) => ({
      total_precio: (product.precio_original || 0) * (product.productos || 0) * exchangeRate,
      total_descuento: (((product.precio_original || 0) - (product.precio || 0)) * (product.productos || 0) * exchangeRate) * (-1),
      total_precio_descuento: (product.precio || 0) * (product.productos || 0) * exchangeRate,
      total_productos: product.productos || 0,
      puntos_recompepsi: (product.puntos_recompepsi || 0) * (product.productos || 0)
    }))
      .map((values: ISummaryPriceResult) => ({
        total_precio: this.operationsS.twoDecimal(values.total_precio),
        total_descuento: this.operationsS.twoDecimal(values.total_descuento),
        total_precio_descuento: this.operationsS.twoDecimal(values.total_precio_descuento),
        total_productos: values.total_productos,
        puntos_recompepsi: values.puntos_recompepsi
      }))
      .reduce((a: ISummaryPriceResult, b: ISummaryPriceResult) => ({
        total_precio: a.total_precio + b.total_precio,
        total_descuento: a.total_descuento + b.total_descuento,
        total_precio_descuento: a.total_precio_descuento + b.total_precio_descuento,
        total_productos: a.total_productos + b.total_productos,
        puntos_recompepsi: a.puntos_recompepsi + b.puntos_recompepsi
      }));
  }

}
